import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../../components/ProductCard/ProductCard";
import productService from "../../api/services/product.service";
// HIDDEN: Product Type filter - may need in future
// import categoriesService from "../../api/services/categories.service";
import { getProductImages } from "../../utils/imageUtils";
import "./Products.css";
import "../../styles/Layout.css";
import "../Home/Home.css";

function mapProductForCard(product) {
  const images = getProductImages(product);
  const productId = product.uuid || product._id || product.product_code || product.id;
  const variants = Array.isArray(product.variants) ? product.variants : [];

  // Filter for valid variants (price > 0)
  const validVariants = variants
    .filter((variant) => {
      const price = parseFloat(variant.variant_price || variant.price || 0);
      return price > 0;
    })
    .sort((a, b) => {
      const getQuantity = (v) => {
        if (Array.isArray(v.attributes)) {
          return v.attributes[0]?.attribute_value || "";
        }
        return Object.values(v.attributes || {})[0] || "";
      };
      const aVal = parseFloat(getQuantity(a));
      const bVal = parseFloat(getQuantity(b));
      if (!isNaN(aVal) && !isNaN(bVal)) return aVal - bVal;
      return String(getQuantity(a)).localeCompare(String(getQuantity(b)));
    });

  // Try to find a default variant with "Pack Quantity" = "3", but only from valid variants
  const defaultVariant =
    validVariants.find(
      (variant) =>
        Array.isArray(variant?.attributes) &&
        variant.attributes.some(
          (attribute) =>
            attribute?.attribute_type === "Pack Quantity" &&
            String(attribute?.attribute_value).trim() === "3"
        )
    ) ||
    validVariants[0] ||
    variants[0];

  const defaultVariantUuid =
    defaultVariant?.variant_uuid || defaultVariant?.uuid || defaultVariant?.variant_id;

  const durations = Array.isArray(defaultVariant?.billing_durations)
    ? defaultVariant.billing_durations
    : [];

  return {
    id: productId,
    name: product.name,
    price: parseFloat(defaultVariant?.variant_price || defaultVariant?.price || product.price || 0) || 0,
    originalPrice: product.original_price,
    image: images.length > 0 ? images[0] : null,
    benefit: product.how_to_use || "Clean energy with a smoother finish.",
    category: product.category_uuid,
    subcategory: product.subcategory_name,
    inStock: product.stock > 0,
    variants,
    defaultVariantUuid,
    defaultBillingDuration: durations.includes(7) ? 7 : durations[0],
    defaultPurchaseType: "one_time",
  };
}

export default function Products() {
  // Use search params for sorting to ensure it persists across hard refreshes and is shareable
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryUuid = searchParams.get("category_uuid");
  const sortByParam = searchParams.get("sort_by") || localStorage.getItem('products-sort-by') || "select-filter";

  const [products, setProducts] = useState([]);
  // HIDDEN: Product Type filter categories state - may need in future
  // const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // HIDDEN: All filter states - may need in future
  // const [selectedCategory, setSelectedCategory] = useState("All");
  // const [selectedSubcategory, setSelectedSubcategory] = useState("All");
  // const [sortBy, setSortBy] = useState("best-sellers");
  // const [priceRange, setPriceRange] = useState("all");
  // const [availability, setAvailability] = useState("all");
  // const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  // const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  // const [style, setStyle] = useState([]);

  const [sortBy, setSortBy] = useState(sortByParam);

  // Sync sorting preference with localStorage and URL
  useEffect(() => {
    localStorage.setItem('products-sort-by', sortBy);
    
    // Update URL without losing other params
    const newParams = new URLSearchParams(searchParams);
    if (sortBy && sortBy !== "select-filter") {
      newParams.set("sort_by", sortBy);
    } else {
      newParams.delete("sort_by");
    }
    setSearchParams(newParams, { replace: true });
  }, [sortBy, setSearchParams, searchParams]);

  // HIDDEN: All filters commented out - may need in future
  // useEffect(() => {
  //   const fetchCategories = async () => {
  //     try {
  //       const res = await categoriesService.userList({ page: 0, limit: 100 });
  //       const body = res?.data ?? res;
  //       let cats = [];
  //       if (Array.isArray(body)) cats = body;
  //       else if (Array.isArray(body?.docs)) cats = body.docs;
  //       else if (Array.isArray(body?.data)) cats = body.data;
  //       else if (Array.isArray(body?.data?.docs)) cats = body.data.docs;
  //       else if (Array.isArray(body?.items)) cats = body.items;
  //       setCategories(cats.filter((cat) => cat.is_active !== false));
  //     } catch (err) {
  //       console.error("Failed to fetch categories:", err);
  //     }
  //   };
  //   fetchCategories();
  // }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await productService.userList({
          page: 0,
          limit: 100,
          category_uuid: categoryUuid,
        });
        const rawProducts = res?.data?.docs || res?.data || [];
        setProducts(rawProducts.map(mapProductForCard));
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categoryUuid]);

  // HIDDEN: All filter effects commented out - may need in future
  // useEffect(() => {
  //   setSelectedCategory(categoryUuid || "All");
  // }, [categoryUuid]);
  // useEffect(() => {
  //   setSelectedSubcategory(subcategoryParam || "All");
  // }, [subcategoryParam]);
  // useEffect(() => {
  //   let count = 0;
  //   if (availability !== "all") count++;
  //   if (selectedCategory !== "All") count++;
  //   if (selectedSubcategory !== "All") count++;
  //   if (priceRange !== "all") count++;
  //   if (style.length > 0) count++;
  //   setActiveFiltersCount(count);
  // }, [availability, selectedCategory, selectedSubcategory, priceRange, style]);

  const sortOptions = [
    { value: "select-filter", label: "Select Filter" },
    { value: "name-asc", label: "Name: A to Z" },
    { value: "name-desc", label: "Name: Z to A" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "price-high", label: "Price: High to Low" },
  ];

  // HIDDEN: Price ranges for filter - may need in future
  // const priceRanges = [
  //   { value: "all", label: "All Prices" },
  //   { value: "under-5", label: "Under $5" },
  //   { value: "5-to-10", label: "$5 - $10" },
  //   { value: "over-10", label: "Over $10" },
  // ];

  // HIDDEN: All filters commented out - may need in future
  // const filteredProducts = [...products]
  //   .filter((product) => {
  //     if (availability === "in-stock") return product.inStock !== false;
  //     if (availability === "out-of-stock") return product.inStock === false;
  //     return true;
  //   })
  //   .filter((product) => {
  //     if (style.length === 0) return true;
  //     return style.some((value) => {
  //       if (value === "relax") return product.name.toLowerCase().includes("relax");
  //       if (value === "euphoria") return product.name.toLowerCase().includes("euphoria");
  //       if (value === "energy") return product.name.toLowerCase().includes("energy");
  //       return false;
  //     });
  //   })
  //   .filter((product) => (selectedCategory !== "All" ? product.category === selectedCategory : true))
  //   .filter((product) => (selectedSubcategory !== "All" ? product.subcategory === selectedSubcategory : true))
  //   .filter((product) => {
  //     if (priceRange === "under-5") return product.price < 5;
  //     if (priceRange === "5-to-10") return product.price >= 5 && product.price <= 10;
  //     if (priceRange === "over-10") return product.price > 10;
  //     return true;
  //   })
  //   .sort((a, b) => {
  //     if (sortBy === "name-asc") return a.name.localeCompare(b.name);
  //     if (sortBy === "name-desc") return b.name.localeCompare(a.name);
  //     if (sortBy === "price-low") return a.price - b.price;
  //     if (sortBy === "price-high") return b.price - a.price;
  //     return String(a.id).localeCompare(String(b.id));
  //   });

  const filteredProducts = [...products]
    .sort((a, b) => {
      if (sortBy === "name-asc") return a.name.localeCompare(b.name);
      if (sortBy === "name-desc") return b.name.localeCompare(a.name);
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      // Default / Select Filter sorting (by ID or original order)
      return 0; 
    });

  // HIDDEN: All filter functions commented out - may need in future
  // const clearAllFilters = () => {
  //   setAvailability("all");
  //   setSelectedCategory("All");
  //   setSelectedSubcategory("All");
  //   setPriceRange("all");
  //   setStyle([]);
  //   setSearchParams({});
  // };
  // const handleStyleChange = (styleValue) => {
  //   setStyle((prev) =>
  //     prev.includes(styleValue)
  //       ? prev.filter((entry) => entry !== styleValue)
  //       : [...prev, styleValue]
  //   );
  // };

  return (
    <div>
      <section className="products-hero">
        <div className="products-hero-container">
          <div className="products-header">
            <h1 className="products-title">
              Shop <span className="highlight">Products</span>
            </h1>
            <div className="about-hero-underline"></div>
            <div className="products-subtitle">
              Discover lab-tested botanicals that deliver extraordinary results. Professional
              wellness without compromise.
            </div>
          </div>
        </div>
      </section>

      <section className="products-main">
        <div className="products-container">
          <div className="products-layout products-full-width">
            <main className="products-content">
              <div className="sort-bar">
                <div className="sort-bar-right">
                  <div className="sort-dropdown">
                    <label>Sort by</label>
                    <select 
                      value={sortBy} 
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select-professional"
                    >
                      <option value="select-filter">Select Filter</option>
                      {sortOptions.filter(opt => opt.value !== 'select-filter').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="product-count">{filteredProducts.length} products</div>
                </div>
              </div>

              <div className="products-grid">
                {loading ? (
                  <div className="products-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading products...</p>
                  </div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((product, index) => (
                    <ProductCard key={product.id} product={product} index={index} />
                  ))
                ) : (
                  <div className="products-empty">
                    <div className="empty-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 12L20 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 12V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 12L4 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.3"/>
                      </svg>
                    </div>
                    <h3>Coming Soon</h3>
                    <p>We're curating the best products for this category. Check back shortly!</p>
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}



